import { LabelDecoration } from "PosterEditor/model/poster-types";
import { Coordinate } from "PosterEditor/hooks/use-coordinates";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { useEditorUI } from "PosterEditor/use-editor-ui";
import styles from "./toolbox-label-style.module.css";
import { Toolbox } from "PosterEditor/sidebar/toolboxes/toolbox";
import { filterClasses } from "PosterEditor/utils/filter-classes";
import {
  ToolboxComponentSection as Section,
  ToolboxSectionDivider as Divider,
} from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-section";
import { ToolboxReverseGeocodeSuggestions } from "PosterEditor/sidebar/toolboxes/pre-made-toolboxes/toolbox-reverse-geocode-suggestions/toolbox-reverse-geocode-suggestions";
import {
  MapLabel,
  MapLabelStyle,
  MapLabelType,
  TextAlignment,
} from "PosterEditor/model/poster-project";
import { ToolboxComponentColorPicker as ColorPicker } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-color-picker";
import { ToolboxComponentSlider as Slider } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-slider";
import { pickerColors } from "PosterEditor/model/poster-project";
import { useState, useEffect } from "react";
import { ToolboxComponentInput } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-input";
import { ToolboxComponentFontPicker as FontPicker } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-font-picker";
import {
  fontNames,
  MapLabelDecorationStyle,
} from "PosterEditor/model/poster-project";
import { useImageStore } from "entity-store/image-store/use-image-store";
import { SaveImageConfig } from "entity-store/image-store/use-image-store-remote";

import { UIIcon } from "PosterEditor/common-components/ui-icon-drawing/ui-icon";
import { UIIconName } from "PosterEditor/common-components/ui-icon-drawing/ui-icon-type";
import { UIIconButton } from "PosterEditor/common-components/ui-icon-drawing/ui-icon-button";
import { ToolboxComponentVisibilityToggle } from "../../toolbox-components/toolbox-component-visibility-toggle";
import { ActivityIndicator } from "BEDROCK/shared-components/activity-indicator";
import { v4 as uuid } from "uuid";
import { StyledPoint } from "PosterEditor/model/poster-project";
import { Shadow } from "PosterEditor/model/poster-types";
import { defaultShadow } from "PosterEditor/model/default-project-style";
import { CTAButton } from "BEDROCK/shared-components/cta-button";
import { UploadedPhotosDialog } from "../asset-import/images-section/uploaded-photos-dialog";

interface ToolboxLabelStyleProps {
  point: StyledPoint;
  onChangePoint: () => void;
}

export const ToolboxMapLabelStyle = (props: ToolboxLabelStyleProps) => {
  const {
    project,
    setStyledPoint,
    styledPointWithID,
    applyBorderAndPointerStyleToAllPoints,
  } = usePosterProject();

  const [label, setLabel] = useState<MapLabel | undefined>(props.point?.label);

  const [actualFont, setActualFont] = useState<string | undefined>();
  const [previewingFont, setPreviewingFont] = useState<string | undefined>();

  const { setEditingElement } = useEditorUI();

  //This is for turning the shadow on and off.  When you turn it on, it should
  //maintain it's previous style.
  const [cachedShadow, setCachedShadow] = useState<Shadow | undefined>(
    label?.style.shadow ?? defaultShadow
  );

  useEffect(() => {
    setLabel(props.point?.label);
    setActualFont(props.point?.label?.style.font ?? "Arial");
  }, [props.point, props.point?.label?.id, props.point?.label?.coordinate]);

  useEffect(() => {
    updateLabel(undefined, undefined, undefined);
  }, [previewingFont, actualFont]);

  const updateLabel = (
    text?: string,
    type?: MapLabelType,
    style?: {
      showImage?: boolean;
      showText?: boolean;
      backgroundColor?: string;
      backgroundOpacity?: number;
      showPointer?: boolean;
      pointerColor?: string;
      pointerThickness?: number;
      padding?: number;
      textColor?: string;
      fontSize?: number;
      fontName?: string;
      decorationStyle?: MapLabelDecorationStyle;
      borderRadius?: number;
      borderWidth?: number;
      borderColor?: string;
      imageSize?: number;
      imageName?: string;
      isUppercase?: boolean;
      // Need an extra property here to handle not being able to pass undefined
      // to remove the shadow.
      hideShadow?: boolean;
      shadow?: Shadow;
      alignment?: TextAlignment;
      lineSpacing?: number;
    }
  ) => {
    if (!label) {
      return;
    }

    const updatedLabel: MapLabel = {
      ...label,
      coordinate: label.coordinate,
      text: text ?? label.text,
      type: type ?? label.type,
      pointID: label.pointID,
      style: {
        ...label.style,
        showImage: style?.showImage ?? label.style.showImage,
        showText: style?.showText ?? label.style.showText,
        offset: label.style.offset ?? { x: 0, y: 0 },
        backgroundColor: style?.backgroundColor ?? label.style.backgroundColor,
        backgroundOpacity:
          style?.backgroundOpacity ?? label.style.backgroundOpacity,
        decorationStyle: style?.decorationStyle ?? label.style.decorationStyle,
        showPointer: style?.showPointer ?? label.style.showPointer,
        pointerColor: style?.pointerColor ?? label.style.pointerColor,
        pointerThickness:
          style?.pointerThickness ?? label.style.pointerThickness,
        borderRadius: style?.borderRadius ?? label.style.borderRadius,
        borderWidth: style?.borderWidth ?? label.style.borderWidth,
        borderColor: style?.borderColor ?? label.style.borderColor,
        padding: style?.padding ?? label.style.padding,
        textColor: style?.textColor ?? label.style.textColor,
        fontSize: style?.fontSize ?? label.style.fontSize,
        font: previewingFont ?? actualFont ?? label.style.font,
        imageSize: style?.imageSize ?? label.style.imageSize,
        imageName: style?.imageName ?? label.style.imageName,
        isUppercase: style?.isUppercase ?? label.style.isUppercase,
        shadow: style?.shadow ?? label.style.shadow ?? defaultShadow,
        alignment: style?.alignment ?? label.style.alignment,
        lineSpacing: style?.lineSpacing ?? label.style.lineSpacing,
      },
    };
    if (style?.hideShadow) {
      setCachedShadow(updatedLabel.style.shadow);
    }
    setLabel(updatedLabel);

    if (props.point) {
      const updatedPoint: StyledPoint = {
        ...props.point,
        label: updatedLabel,
      };
      setStyledPoint(updatedPoint);

      props.onChangePoint();
    }
  };

  const toggleShadowVisibility = () => {
    if (!label?.style.shadow) {
      return;
    }
    const newShadow: Shadow = {
      ...label.style.shadow,
      isHidden: !label.style.shadow.isHidden,
    };

    const updatedLabel: MapLabel = {
      ...label,
      style: {
        ...label.style,
        shadow: newShadow,
      },
    };
    updateLabel(undefined, undefined, {
      shadow: newShadow,
    });
  };

  if (!label) {
    return <div>No label</div>;
  }

  return (
    <div className={styles.container}>
      <Toolbox collapsible={false} showTitleBar={false}>
        <div className={styles.paddedBodyContent}>
          <ToolboxComponentVisibilityToggle
            text="Photo"
            isVisible={label.style.showImage}
            onToggle={() => {
              updateLabel(undefined, undefined, {
                showImage: !label.style.showImage,
              });
            }}
          />

          <PhotoSection
            projectID={project.id}
            label={label}
            size={label.style.imageSize}
            onChangeSize={(size) => {
              updateLabel(undefined, undefined, {
                imageSize: size,
              });
            }}
            onChangeImageName={(imageName) => {
              updateLabel(undefined, undefined, {
                imageName: imageName,
                showImage: true,
              });
            }}
            isEnabled={label.style.showImage}
          />

          <Divider padding={0} />
          <ToolboxComponentVisibilityToggle
            text="Text"
            isVisible={label.style.showText}
            onToggle={() => {
              updateLabel(undefined, undefined, {
                showText: !label.style.showText,
              });
            }}
          />

          <TextSection
            label={label}
            onChangeText={(text) => {
              updateLabel(text, undefined, {
                showText: true,
              });
            }}
            isUppercase={label.style.isUppercase ?? false}
            onSetUppercase={(isUppercase) => {
              updateLabel(undefined, undefined, {
                isUppercase: isUppercase,
              });
            }}
            alignment={label.style.alignment}
            onChangeAlignment={(alignment) => {
              updateLabel(undefined, undefined, {
                alignment: alignment,
              });
            }}
            lineSpacing={label.style.lineSpacing ?? 100}
            onChangeLineSpacing={(lineSpacing) => {
              updateLabel(undefined, undefined, {
                lineSpacing: lineSpacing,
              });
            }}
            coordinate={label.coordinate}
            font={actualFont ?? "Arial"}
            textColor={label.style.textColor ?? "#000000"}
            fontSize={label.style.fontSize ?? 16}
            onFontChange={(font) => {
              setActualFont(font);
              setPreviewingFont(undefined);
            }}
            onFontPreview={(fontName) => {
              setPreviewingFont(fontName);
            }}
            onChangeTextColor={(color) => {
              updateLabel(undefined, undefined, {
                textColor: color,
              });
            }}
            onChangeFontSize={(fontSize) => {
              updateLabel(undefined, undefined, {
                fontSize: fontSize,
              });
            }}
            decorationStyle={label.style.decorationStyle}
            onChangeDecorationStyle={(decorationStyle) => {
              updateLabel(undefined, undefined, {
                decorationStyle: decorationStyle,
              });
            }}
          />

          <Divider padding={0} />
          <CommonSection
            label={label}
            backgroundColor={label.style.backgroundColor}
            onChangeBackgroundColor={(color) => {
              updateLabel(undefined, undefined, {
                backgroundColor: color,
              });
            }}
            backgroundOpacity={label.style.backgroundOpacity ?? 1}
            onChangeBackgroundOpacity={(opacity) => {
              updateLabel(undefined, undefined, {
                backgroundOpacity: opacity,
              });
            }}
            showPointer={label.style.showPointer ?? false}
            onToggleShowPointer={() => {
              const show = !label.style.showPointer;
              updateLabel(undefined, undefined, {
                showPointer: show,
              });
            }}
            pointerColor={label.style.pointerColor ?? "#000000"}
            onChangePointerColor={(color) => {
              updateLabel(undefined, undefined, {
                pointerColor: color,
              });
            }}
            pointerThickness={label.style.pointerThickness ?? 1}
            onChangePointerThickness={(thickness) => {
              updateLabel(undefined, undefined, {
                pointerThickness: thickness,
              });
            }}
            padding={label.style.borderWidth ?? 0}
            onChangePadding={(padding) => {
              updateLabel(undefined, undefined, {
                borderWidth: padding,
              });
            }}
            borderRadius={label.style.borderRadius ?? 0}
            onChangeBorderRadius={(radius) => {
              updateLabel(undefined, undefined, {
                borderRadius: radius,
              });
            }}
          />
          <Divider padding={4} />
          <ShadowSection
            showShadow={label.style.shadow?.isHidden !== true}
            onToggleShowShadow={() => {
              toggleShadowVisibility();
            }}
            color={label.style.shadow?.color ?? "#000000"}
            onChangeColor={(color) => {
              updateLabel(undefined, undefined, {
                shadow: {
                  ...(label.style.shadow ?? defaultShadow),
                  color: color,
                },
              });
            }}
            blur={label.style.shadow?.blur ?? 7}
            onChangeBlur={(blur) => {
              updateLabel(undefined, undefined, {
                shadow: {
                  ...(label.style.shadow ?? defaultShadow),
                  blur: blur,
                },
              });
            }}
            opacity={label.style.shadow?.opacity ?? 0.5}
            onChangeOpacity={(opacity) => {
              updateLabel(undefined, undefined, {
                shadow: {
                  ...(label.style.shadow ?? defaultShadow),
                  opacity: opacity,
                },
              });
            }}
            offset={label.style.shadow?.offset.height ?? 10}
            onChangeOffset={(offset) => {
              updateLabel(undefined, undefined, {
                shadow: {
                  ...(label.style.shadow ?? defaultShadow),
                  offset: { width: 0, height: offset },
                },
              });
            }}
          />
          <Divider />
          <CTAButton
            variant="LINK-SECONDARY"
            title="Apply style to all labels"
            onClick={() => {
              applyBorderAndPointerStyleToAllPoints(label.style);
            }}
          />
          <Divider whitespaceOnly />
        </div>
      </Toolbox>
    </div>
  );
};

interface CommonSectionProps {
  label: MapLabel;
  backgroundColor: string;
  onChangeBackgroundColor: (color: string) => void;
  backgroundOpacity: number;
  onChangeBackgroundOpacity: (opacity: number) => void;
  showPointer: boolean;
  onToggleShowPointer: () => void;
  pointerColor: string;
  onChangePointerColor: (color: string) => void;
  pointerThickness: number;
  onChangePointerThickness: (thickness: number) => void;
  padding: number;
  onChangePadding: (padding: number) => void;
  borderRadius: number;
  onChangeBorderRadius: (radius: number) => void;
}

const CommonSection = (props: CommonSectionProps) => {
  const [isBackgroundColorExpanded, setIsBackgroundColorExpanded] =
    useState(false);
  const [isPointerColorExpanded, setIsPointerColorExpanded] = useState(false);

  const { isTemplateEditing } = usePosterProject();
  const { project } = usePosterProject();
  return (
    <Section title="Background & Pointer">
      <>
        <div className={styles.toolboxRowContainer}>
          {(isTemplateEditing ||
            project.editingPermissions.mapLabel.backgroundColor) && (
            <ColorPicker
              showSwatch={true}
              colors={pickerColors}
              selectedColor={props.backgroundColor}
              onSelectColor={props.onChangeBackgroundColor}
              isExpanded={isBackgroundColorExpanded}
              onToggleExpand={() =>
                setIsBackgroundColorExpanded((prev) => !prev)
              }
            />
          )}
          {(isTemplateEditing ||
            project.editingPermissions.mapLabel.backgroundOpacity) && (
            <Slider
              title="Background"
              min={0}
              max={100}
              value={props.backgroundOpacity * 100}
              onChange={(opacityPerc) => {
                const opacity = opacityPerc / 100;
                props.onChangeBackgroundOpacity(opacity);
              }}
            />
          )}
        </div>
        {(isTemplateEditing ||
          project.editingPermissions.mapLabel.backgroundPadding) && (
          <Slider
            title="Padding"
            min={0}
            max={25}
            value={props.padding}
            onChange={(value) => {
              props.onChangePadding(value);
            }}
          />
        )}
        {(isTemplateEditing ||
          project.editingPermissions.mapLabel.backgroundPadding) && (
          <Slider
            title="Corner radius"
            min={0}
            max={100}
            value={props.borderRadius}
            onChange={(value) => {
              props.onChangeBorderRadius(value);
            }}
          />
        )}
        {(isTemplateEditing ||
          project.editingPermissions.mapLabel.backgroundPadding) && (
          <>
            <Divider padding={4} />

            <ToolboxComponentVisibilityToggle
              text="Pointer"
              isVisible={props.showPointer}
              onToggle={() => {
                props.onToggleShowPointer();
              }}
            />

            <div className={styles.toolboxRowContainer}>
              <ColorPicker
                disabled={!props.showPointer}
                showSwatch={true}
                colors={pickerColors}
                selectedColor={props.pointerColor}
                onSelectColor={props.onChangePointerColor}
                isExpanded={isPointerColorExpanded}
                onToggleExpand={() =>
                  setIsPointerColorExpanded((prev) => !prev)
                }
              />
              <Slider
                disabled={!props.showPointer}
                title="Pointer"
                min={0}
                max={12}
                value={props.pointerThickness}
                onChange={(value) => {
                  props.onChangePointerThickness(value);
                }}
              />
            </div>
          </>
        )}
      </>
    </Section>
  );
};

interface PhotoSectionProps {
  projectID: string;
  label: MapLabel;
  size: number;
  onChangeSize: (size: number) => void;
  onChangeImageName: (imageName: string) => void;
  isEnabled: boolean;
}

const PhotoSection = (props: PhotoSectionProps) => {
  const { cacheImageForMapLabelStyle, imageCache, isTemplateEditing } =
    usePosterProject();
  const { copyImageFromPublicToUser, duplicateUserImage } = useImageStore();

  const { resizeAndSaveImage } = useImageStore();
  const [isLoading, setIsLoading] = useState(false);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsLoading(true);
    const file = event.target.files?.[0];
    if (!file) return;
    const newImageName = uuid();
    const config: SaveImageConfig = {
      image: file,
      existingFilename: props.label.style.imageName,
      newFilename: newImageName,
      isPublic: isTemplateEditing,
      folder: isTemplateEditing
        ? "labels"
        : `projects/${props.projectID}/labels`,
    };
    await resizeAndSaveImage(config, 500, "jpeg");
    const style: MapLabelStyle = {
      ...props.label.style,
      imageName: newImageName,
    };
    await cacheImageForMapLabelStyle(style);
    props.onChangeImageName(newImageName);
    setIsLoading(false);
  };

  const handleUploadedImageSelect = async (url: string) => {
    setIsLoading(true);
    const filenameWithExtension = url.split("/").pop()?.split("?")[0] || "";
    const filename = filenameWithExtension.replace(/\.[^/.]+$/, "");
    const dest = "projects/" + props.projectID + "/labels";
    await duplicateUserImage("uploads", filename, "jpeg", dest, filename);
    const style: MapLabelStyle = {
      ...props.label.style,
      imageName: filename,
    };
    await cacheImageForMapLabelStyle(style);
    props.onChangeImageName(filename);
    setIsLoading(false);
  };
  const imageElement = imageCache.get(props.label.style.imageName ?? "");

  return (
    <Section>
      <input
        id="file-upload"
        className={styles.uploadButton}
        type="file"
        accept=".png, .jpg, .jpeg"
        onChange={handleFileUpload}
      />
      <div
        className={styles.imageWrapper}
        style={{ opacity: props.isEnabled ? 1 : 0.5 }}
      >
        {imageElement ? (
          <label
            htmlFor="file-upload"
            className={filterClasses(
              styles.photo,
              props.isEnabled === false ? styles.photoDisabled : ""
            )}
          >
            <img
              src={imageElement?.src}
              alt="Label"
              className={styles.imagePreview}
              crossOrigin="anonymous"
            />
            <div className={styles.photoOverlay}>
              <div className={styles.photoOverlayContent}>
                <span className={styles.changeImageText}>CHANGE PHOTO</span>
              </div>
            </div>
          </label>
        ) : (
          <div>
            <label
              htmlFor="file-upload"
              className={filterClasses(
                styles.photo,
                props.isEnabled === false ? styles.photoDisabled : ""
              )}
            >
              {isLoading ? (
                <ActivityIndicator />
              ) : (
                <>
                  <UIIcon iconName="PLUS" variant="REGULAR" size={50} />
                  <span className={styles.imagePlaceholderText}>
                    UPLOAD PHOTO
                  </span>
                </>
              )}
            </label>
          </div>
        )}
      </div>

      <UploadedPhotosDialog onClickImage={handleUploadedImageSelect} />

      {imageElement && (
        <>
          <Slider
            disabled={!props.isEnabled}
            title="Photo size"
            min={10}
            max={100}
            value={props.size}
            onChange={(value) => {
              props.onChangeSize(value);
            }}
          />

          {/* <Section title="Border">
            <div className={styles.toolboxRowContainer}>
              <ColorPicker
                showSwatch={true}
                colors={pickerColors}
                selectedColor={props.borderColor}
                onSelectColor={props.onChangeBorderColor}
                isExpanded={isColorPickerExpanded}
                onToggleExpand={() => setIsColorPickerExpanded((prev) => !prev)}
              />
              <Slider
                title="Corner radius"
                min={0}
                max={100}
                value={props.boderRadius}
                onChange={(value) => {
                  props.onChangeBorderRadius(value);
                }}
              />
            </div>
            <Slider
              title="Width"
              min={0}
              max={100}
              value={props.borderWidth}
              onChange={(value) => {
                props.onChangeBorderWidth(value);
              }}
            />
          </Section> */}
        </>
      )}
    </Section>
  );
};

interface TextSectionProps {
  label: MapLabel;
  font: string;
  textColor: string;
  fontSize: number;
  alignment: TextAlignment;
  decorationStyle: MapLabelDecorationStyle;
  lineSpacing: number;
  onChangeLineSpacing: (lineSpacing: number) => void;
  onChangeDecorationStyle: (style: MapLabelDecorationStyle) => void;
  onFontChange: (font: string) => void;
  onFontPreview: (font: string | undefined) => void;
  onChangeTextColor: (color: string) => void;
  onChangeFontSize: (fontSize: number) => void;
  onChangeText: (text: string) => void;
  isUppercase: boolean;
  onSetUppercase: (isUppercase: boolean) => void;
  onChangeAlignment: (alignment: TextAlignment) => void;
  coordinate: Coordinate;
}

const TextSection = (props: TextSectionProps) => {
  const {
    project,
    isTemplateEditing,
    // setMapLabel: saveLabelToProject,
    // getMapLabel,
    // removeMapLabel,
    setStyledPoint,
  } = usePosterProject();
  const { setEditingElement } = useEditorUI();

  return (
    <div className={styles.section}>
      {(isTemplateEditing || project.editingPermissions.mapLabel.text) && (
        <TextContentControl
          disabled={!props.label.style.showText}
          coordinate={props.label.coordinate}
          text={props.label.text ?? ""}
          onChangeText={props.onChangeText}
          isUppercase={props.label.style.isUppercase ?? false}
          onSetUppercase={props.onSetUppercase}
          onClickDeleteLabel={() => {
            setEditingElement(undefined);
            // removeMapLabel(label);
          }}
        />
      )}
      <TextStyleControl
        disabled={!props.label.style.showText}
        font={props.font}
        fontSize={props.fontSize}
        textColor={props.textColor}
        alignment={props.alignment}
        lineSpacing={props.lineSpacing}
        onChangeLineSpacing={props.onChangeLineSpacing}
        onChangeFontSize={props.onChangeFontSize}
        onFontChange={props.onFontChange}
        onFontPreview={props.onFontPreview}
        onChangeTextColor={props.onChangeTextColor}
        onChangeAlignment={props.onChangeAlignment}
      />
      {!props.label.style.showImage && (
        <DecorationControl
          disabled={!props.label.style.showText}
          style={props.decorationStyle}
          onChangeStyle={props.onChangeDecorationStyle}
        />
      )}
    </div>
  );
};

interface TextContentControlProps {
  text: string;
  onChangeText: (text: string) => void;
  isUppercase: boolean;
  onSetUppercase: (isUppercase: boolean) => void;
  coordinate: Coordinate;
  onClickDeleteLabel: () => void;
  disabled?: boolean;
}

const TextContentControl = (props: TextContentControlProps) => {
  const onToggleUppercase = () => {
    props.onSetUppercase(!props.isUppercase);
  };

  useEffect(() => {
    if (props.isUppercase) {
      const text = props.text.toUpperCase();
      props.onChangeText(text);
    } else {
      const text = props.text.toLocaleLowerCase();
      props.onChangeText(text);
    }
  }, [props.isUppercase]);

  const onChangeText = (text: string) => {
    if (props.isUppercase) {
      text = text.toUpperCase();
    }
    props.onChangeText(text);
  };

  const text = props.isUppercase ? props.text.toUpperCase() : props.text;

  return (
    <Section>
      <div className={styles.toolboxRowContainer}>
        <ToolboxComponentInput
          disabled={props.disabled}
          text={text}
          onChangeText={onChangeText}
          autoFocusOnMount={true}
          lineNumber={3}
        />
        <UIIconButton
          disabled={props.disabled}
          iconName={props.isUppercase ? "TEXT-CASE-ON" : "TEXT-CASE-OFF"}
          padding={10}
          onClick={onToggleUppercase}
        />
      </div>
      <ToolboxReverseGeocodeSuggestions
        disabled={props.disabled}
        coordinate={props.coordinate}
        onSelectLandmarkText={(text) => {
          if (props.isUppercase ?? false) {
            props.onChangeText(text.toUpperCase());
          } else {
            props.onChangeText(text);
          }
        }}
      />
    </Section>
  );
};

interface TextStyleControlProps {
  font: string;
  textColor: string;
  fontSize: number;
  alignment: TextAlignment;
  lineSpacing: number;

  onFontChange: (font: string) => void;
  onFontPreview: (fontName: string | undefined) => void;
  onChangeTextColor: (color: string) => void;
  onChangeFontSize: (fontSize: number) => void;
  onChangeAlignment: (alignment: TextAlignment) => void;
  onChangeLineSpacing: (lineSpacing: number) => void;

  disabled?: boolean;
}

const TextStyleControl = (props: TextStyleControlProps) => {
  const { project, isTemplateEditing } = usePosterProject();
  const [isColorExpanded, setIsColorExpanded] = useState(false);

  return (
    <Section>
      {(isTemplateEditing || project.editingPermissions.mapLabel.font) && (
        <FontPicker
          disabled={props.disabled}
          fontNames={fontNames()}
          selectedFont={props.font ?? "Arial"}
          onPreviewFont={(fontName) => {
            props.onFontPreview(fontName);
          }}
          onSelectFont={(fontName) => {
            props.onFontChange(fontName);
          }}
          className={styles.fontPickerLabel}
        />
      )}
      <div className={styles.toolboxRowContainer}>
        {(isTemplateEditing || project.editingPermissions.mapLabel.color) && (
          <ColorPicker
            disabled={props.disabled}
            showSwatch={true}
            colors={pickerColors}
            selectedColor={props.textColor}
            onSelectColor={props.onChangeTextColor}
            isExpanded={isColorExpanded}
            onToggleExpand={() => setIsColorExpanded((prev) => !prev)}
          />
        )}
        {(isTemplateEditing || project.editingPermissions.mapLabel.size) && (
          <Slider
            disabled={props.disabled}
            title="Text size"
            min={8}
            max={48}
            value={props.fontSize}
            onChange={(value) => {
              props.onChangeFontSize(value);
            }}
          />
        )}
      </div>
      <div className={styles.toolboxRowContainer}>
        <div className={styles.toolboxRowContainer}>
          <UIIconButton
            width={20}
            height={20}
            iconName="TEXT-ALIGN-LEFT"
            isSelected={props.alignment === "left"}
            onClick={() => props.onChangeAlignment("left")}
          />
          <UIIconButton
            width={20}
            height={20}
            iconName="TEXT-ALIGN-CENTER"
            isSelected={props.alignment === "center"}
            onClick={() => props.onChangeAlignment("center")}
          />
          <UIIconButton
            width={20}
            height={20}
            iconName="TEXT-ALIGN-RIGHT"
            isSelected={props.alignment === "right"}
            onClick={() => props.onChangeAlignment("right")}
          />
        </div>
        <Slider
          disabled={props.disabled}
          title="Line spacing"
          min={50}
          max={200}
          value={props.lineSpacing}
          onChange={(value) => {
            props.onChangeLineSpacing(value);
          }}
        />
      </div>
    </Section>
  );
};

interface DecorationControlProps {
  style: MapLabelDecorationStyle;
  onChangeStyle: (decorationType: MapLabelDecorationStyle) => void;
  disabled?: boolean;
}

const DecorationControl = (props: DecorationControlProps) => {
  const [isColorPickerExpanded, setIsColorExpanded] = useState(false);
  const availableDecorationTypes: LabelDecoration[] = [
    "none",
    "border",
    "line",
    "bracket",
    "dot",
    "corner",
  ];

  const iconTypeForDecoration = (decoration: LabelDecoration): UIIconName => {
    switch (decoration) {
      case "border":
        return "LABEL-DECORATION-BORDER";
      case "line":
        return "LABEL-DECORATION-LINE";
      case "bracket":
        return "LABEL-DECORATION-BRACKET";
      case "dot":
        return "LABEL-DECORATION-DOT";
      case "corner":
        return "LABEL-DECORATION-CORNER";
      default:
        return "LABEL-DECORATION-NONE";
    }
  };

  const updateDecoration = (style: {
    decorationType?: LabelDecoration;
    color?: string;
    length?: number;
    width?: number;
  }) => {
    const updatedDecoration: MapLabelDecorationStyle = {
      decoration: style.decorationType ?? props.style.decoration,
      color: style.color ?? props.style.color,
      length: style.length ?? props.style.length,
      width: style.width ?? props.style.width,
    };
    props.onChangeStyle(updatedDecoration);
  };

  const selectedColor = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--color-selected");

  const interactiveColor = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--color-interactive");

  const selctedStyleSupportLength =
    props.style.decoration !== "line" && props.style.decoration !== "dot";

  return (
    <Section title="Label decoration">
      <div className={styles.decorationTypeContainer}>
        {availableDecorationTypes.map((decorationType) => (
          <UIIconButton
            disabled={props.disabled}
            width={40}
            height={40}
            iconName={iconTypeForDecoration(decorationType)}
            onClick={() => {
              const width = props.style.width === 0 ? 0.5 : props.style.width;
              const length =
                props.style.length === 0 ? 0.5 : props.style.length;
              updateDecoration({ decorationType, length: length, width });
            }}
            isSelected={props.style.decoration === decorationType}
            // className={styles.decorationTypeButton}
          />
        ))}
      </div>
      <div className={styles.toolboxRowContainer}>
        <ColorPicker
          disabled={props.disabled}
          showSwatch={true}
          isExpanded={isColorPickerExpanded}
          onToggleExpand={() => setIsColorExpanded((prev) => !prev)}
          colors={pickerColors}
          selectedColor={props.style.color}
          onSelectColor={(color) => {
            updateDecoration({ color });
          }}
        />
        <Slider
          disabled={props.disabled}
          title="Width"
          min={0}
          max={100}
          value={props.style.width * 100}
          onChange={(value) => {
            updateDecoration({ width: value / 100 });
          }}
        />
      </div>
      {selctedStyleSupportLength && (
        <Slider
          disabled={props.disabled}
          title="Length"
          min={0}
          max={100}
          value={props.style.length * 100}
          onChange={(value) => {
            updateDecoration({ length: value / 100 });
          }}
        />
      )}
    </Section>
  );
};

interface ShadowSectionProps {
  showShadow: boolean;
  onToggleShowShadow: () => void;
  color: string;
  onChangeColor: (color: string) => void;
  opacity: number;
  onChangeOpacity: (opacity: number) => void;
  offset: number;
  onChangeOffset: (offset: number) => void;
  blur: number;
  onChangeBlur: (blur: number) => void;
}

const ShadowSection = (props: ShadowSectionProps) => {
  const [isColorExpanded, setIsColorExpanded] = useState<boolean>(false);

  // const updateShadow = (style: {
  //   color?: string;
  //   offset?: number;
  //   blur?: number;
  //   opacity?: number;
  // }) => {
  //   const newShadow: Shadow = {
  //     color: style.color ?? shadow?.color ?? "rgba(0, 0, 0, 0.5)",
  //     offset: { width: 0, height: style.offset ?? shadow?.offset.height ?? 10 },
  //     blur: style.blur ?? shadow?.blur ?? 7,
  //     opacity: style.opacity ?? shadow?.opacity ?? 0.5,
  //   };
  //   setShadow(newShadow);
  //   setLineShadow(newShadow);
  // };

  return (
    <Section>
      <ToolboxComponentVisibilityToggle
        text="Shadow"
        isVisible={props.showShadow}
        onToggle={() => {
          props.onToggleShowShadow();
        }}
      />

      <ColorPicker
        disabled={!props.showShadow}
        title="Color"
        showSwatch={true}
        colors={pickerColors}
        selectedColor={props.color}
        onHoverColor={() => {}}
        onSelectColor={(color) => {
          props.onChangeColor(color);
        }}
        isExpanded={isColorExpanded}
        onToggleExpand={() => {
          setIsColorExpanded((prev) => !prev);
        }}
      />
      <Slider
        disabled={!props.showShadow}
        title="Opacity"
        min={0}
        max={100}
        value={props.opacity * 100}
        onChange={(value) => {
          props.onChangeOpacity(value / 100);
        }}
      />
      <Slider
        disabled={!props.showShadow}
        title="Depth"
        min={0}
        max={100}
        value={props.offset * 5}
        onChange={(value) => {
          props.onChangeOffset(value / 5);
        }}
      />
      <Slider
        disabled={!props.showShadow}
        title="Blur"
        min={0}
        max={100}
        value={props.blur * 5}
        onChange={(value) => {
          props.onChangeBlur(value / 5);
        }}
      />
    </Section>
  );
};
